import { useState, useEffect } from 'react';
import './QuoteDetail.css';
import { apiFetch } from '../../utils/api';

const STATUS_COLORS = {
  new:         { bg: 'rgba(255,183,206,0.12)', border: 'rgba(255,183,206,0.35)', text: '#FFB7CE' },
  contacted:   { bg: 'rgba(100,180,255,0.12)', border: 'rgba(100,180,255,0.35)', text: '#64b4ff' },
  in_progress: { bg: 'rgba(120,220,160,0.12)', border: 'rgba(120,220,160,0.35)', text: '#78dca0' },
  closed:      { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', text: 'rgba(255,255,255,0.35)' },
}

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', in_progress: 'In Progress', closed: 'Closed',
}

const SERVICES_OPTIONS = [
  'Software Development', 'UI/UX Design', 'Social Media Management',
  'SEO', 'Mentorship & Training', 'Ready-Made Solutions',
]

const BUDGETS = [
  'Under KSh 50,000', 'KSh 50,000 – 150,000', 'KSh 150,000 – 500,000',
  'KSh 500,000 – 1,500,000', 'KSh 1,500,000+', 'Not sure yet',
]

const TIMELINES = [
  '1 – 2 weeks (Rush — extra cost applies)',
  '3 – 4 weeks', '1 – 2 months', '3 – 6 months', '6+ months', 'Flexible',
]

const TIME_SLOTS = ['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00']
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DAY_LABELS  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

function fmtTime(t) {
  const h = parseInt(t.split(':')[0])
  return h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h-12}:00 PM`
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function initials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

function normalizeServices(s) {
  if (Array.isArray(s)) return s.filter(Boolean)
  return (s || '').split(',').map(x => x.trim()).filter(Boolean)
}

// ── Mini booking calendar for admin edit mode ─────────────────────
function BookingPicker({ currentDate, currentTime, excludeBookingId, onPick }) {
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`
  const [viewYear,  setViewYear]  = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [selDate,   setSelDate]   = useState(currentDate || null)
  const [selTime,   setSelTime]   = useState(currentTime || null)
  const [taken,     setTaken]     = useState([])

  useEffect(() => {
    const y = viewYear, m = viewMonth
    const start = `${y}-${String(m+1).padStart(2,'0')}-01`
    const last  = new Date(y, m+1, 0).getDate()
    const end   = `${y}-${String(m+1).padStart(2,'0')}-${last}`
    apiFetch(`/api/bookings/taken?start=${start}&end=${end}`)
      .then(r => r.json())
      .then(d => setTaken(d.taken || []))
      .catch(() => setTaken([]))
  }, [viewMonth, viewYear])

  // Exclude current booking slot so it doesn't block itself
  const isTaken = (date, time) =>
    taken.some(s => s.date === date && s.time === time &&
      !(date === currentDate && time === currentTime))

  const cellDate  = (d) => `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
  const isSunday  = (d) => new Date(viewYear, viewMonth, d).getDay() === 0
  const isPast    = (d) => cellDate(d) < todayStr
  const isFullDay = (d) => TIME_SLOTS.every(t => isTaken(cellDate(d), t))

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear(y => y - 1)
    } else {
      setViewMonth(m => m - 1)
    }

    setSelDate(null)
    setSelTime(null)
  }

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear(y => y + 1)
    } else {
      setViewMonth(m => m + 1)
    }

    setSelDate(null)
    setSelTime(null)
  }

  const firstDow  = new Date(viewYear, viewMonth, 1).getDay()
  const startOff  = firstDow === 0 ? 6 : firstDow - 1
  const daysInMon = new Date(viewYear, viewMonth+1, 0).getDate()
  const cells     = [...Array(startOff).fill(null), ...Array.from({length:daysInMon},(_,i)=>i+1)]

  const pickDate = (ds) => { setSelDate(ds); setSelTime(null) }
  const pickTime = (t)  => {
    setSelTime(t)
    onPick(selDate, t)
  }

  return (
    <div className='qd-bp'>
      {/* Month nav */}
      <div className='qd-bp-nav'>
        <button onClick={prevMonth}>‹</button>
        <span>{MONTHS_FULL[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth}>›</button>
      </div>

      {/* Day labels */}
      <div className='qd-bp-grid qd-bp-heads'>
        {DAY_LABELS.map((d,i) => (
          <div key={i} className={`qd-bp-head ${i===6?'qd-bp-head--sun':''}`}>{d}</div>
        ))}
      </div>

      {/* Dates */}
      <div className='qd-bp-grid'>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`}/>
          const ds       = cellDate(day)
          const sun      = isSunday(day)
          const past     = isPast(day)
          const full     = !sun && !past && isFullDay(day)
          const disabled = sun || past || full
          const selected = ds === selDate
          return (
            <button
              key={ds}
              className={['qd-bp-day', selected?'qd-bp-day--sel':'', disabled?'qd-bp-day--dis':'', sun?'qd-bp-day--sun':''].filter(Boolean).join(' ')}
              onClick={() => !disabled && pickDate(ds)}
              disabled={disabled}
            >
              {day}
            </button>
          )
        })}
      </div>

      {/* Time slots */}
      {selDate && (
        <div className='qd-bp-slots'>
          <p className='qd-bp-slots-label'>Times for <strong>{selDate}</strong></p>
          <div className='qd-bp-slots-grid'>
            {TIME_SLOTS.map(t => {
              const taken_  = isTaken(selDate, t)
              const active  = selTime === t
              return (
                <button
                  key={t}
                  className={['qd-bp-slot', active?'qd-bp-slot--active':'', taken_?'qd-bp-slot--taken':''].filter(Boolean).join(' ')}
                  onClick={() => !taken_ && pickTime(t)}
                  disabled={taken_}
                >
                  {fmtTime(t)}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main QuoteDetail panel ────────────────────────────────────────
export default function QuoteDetail({ quote, onClose, onStatusChange }) {
  const [status,  setStatus]  = useState(quote.status)
  const [saving,  setSaving]  = useState(false)
  const [booking, setBooking] = useState(null)
  const [editMode, setEdit]   = useState(false)
  const [saveErr,  setSaveErr] = useState(null)

  // Edit form state
  const [form, setForm] = useState({
    name:         quote.name         || '',
    phone:        quote.phone        || '',
    email:        quote.email        || '',
    company:      quote.company      || '',
    company_desc: quote.company_desc || '',
    services:     normalizeServices(quote.services),
    requirement:  quote.requirement  || '',
    budget:       quote.budget       || BUDGETS[0],
    timeline:     quote.timeline     || TIMELINES[0],
    notes:        quote.notes        || '',
  })
  const [newDate, setNewDate] = useState(null)
  const [newTime, setNewTime] = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const toggleService = (s) => {
    setForm(f => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter(x => x !== s)
        : [...f.services, s],
    }))
  }

  useEffect(() => {
    apiFetch('/api/admin/bookings')
      .then(r => r.json())
      .then(d => {
        const found = (d.bookings || []).find(b => b.quote_id === quote.id)
        if (found) setBooking(found)
      })
      .catch(() => {})
  }, [quote.id])

  const changeStatus = async (newStatus) => {
    if (newStatus === status) return
    setSaving(true)
    try {
      await apiFetch(`/api/quotes/${quote.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      })
      setStatus(newStatus)
      onStatusChange(quote.id, newStatus)
    } catch { /* silent */ }
    finally { setSaving(false) }
  }

  const saveEdits = async () => {
    if (!form.name || !form.email) { setSaveErr('Name and email are required.'); return }
    setSaving(true); setSaveErr(null)
    try {
      // 1. Update quote fields
      await apiFetch(`/api/quotes/${quote.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          services: form.services,  // sent as array, backend joins
        }),
      })

      // 2. Update booking if date/time changed
      if (newDate && newTime) {
        // Delete existing booking for this quote
        if (booking) {
          await apiFetch(`/api/admin/bookings/${booking.id}`, { method: 'DELETE' })
        }
        // Create new booking
        await apiFetch('/api/bookings', {
          method: 'POST',
          body: JSON.stringify({
            quote_id:     quote.id,
            name:         form.name,
            email:        form.email,
            booking_date: newDate,
            booking_time: newTime,
          }),
        })
        setBooking({ booking_date: newDate, booking_time: newTime, quote_id: quote.id })
        setNewDate(null); setNewTime(null)
      }

      // Update parent quote data visually
      onStatusChange(quote.id, status, { ...form, services: form.services.join(', ') })
      setEdit(false)
    } catch (e) {
      setSaveErr(e.message || 'Save failed.')
    } finally { setSaving(false) }
  }

  const cancelEdit = () => {
    setForm({
      name:         quote.name         || '',
      phone:        quote.phone        || '',
      email:        quote.email        || '',
      company:      quote.company      || '',
      company_desc: quote.company_desc || '',
      services:     normalizeServices(quote.services),
      requirement:  quote.requirement  || '',
      budget:       quote.budget       || BUDGETS[0],
      timeline:     quote.timeline     || TIMELINES[0],
      notes:        quote.notes        || '',
    })
    setNewDate(null); setNewTime(null); setSaveErr(null)
    setEdit(false)
  }

  return (
    <div className='qd-overlay' onClick={onClose}>
      <div className='qd-panel' onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className='qd-header'>
          <div className='qd-avatar'>{initials(quote.name)}</div>
          <div className='qd-header-info'>
            <h2>{editMode ? form.name || quote.name : quote.name}</h2>
            <span>{quote.company || 'No company listed'}</span>
          </div>
          <div className='qd-header-actions'>
            {!editMode && (
              <button className='qd-edit-btn' onClick={() => setEdit(true)} title='Edit quote'>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>
            )}
            <button className='qd-close' onClick={onClose} aria-label='Close'>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div className='qd-body'>

          {editMode ? (
            /* ══ EDIT MODE ══════════════════════════════ */
            <>
              <div className='qd-section'>
                <p className='qd-label'>Client Details</p>
                <div className='qd-edit-row'>
                  <div className='qd-edit-field'>
                    <label>Name</label>
                    <input value={form.name} onChange={e=>set('name',e.target.value)} placeholder='Full name'/>
                  </div>
                  <div className='qd-edit-field'>
                    <label>Phone</label>
                    <input value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder='+254...'/>
                  </div>
                </div>
                <div className='qd-edit-row'>
                  <div className='qd-edit-field'>
                    <label>Email</label>
                    <input type='email' value={form.email} onChange={e=>set('email',e.target.value)} placeholder='email@...'/>
                  </div>
                  <div className='qd-edit-field'>
                    <label>Company</label>
                    <input value={form.company} onChange={e=>set('company',e.target.value)} placeholder='Company name'/>
                  </div>
                </div>
                <div className='qd-edit-field'>
                  <label>Company Description</label>
                  <textarea rows={2} value={form.company_desc} onChange={e=>set('company_desc',e.target.value)} placeholder='What does their company do?'/>
                </div>
              </div>

              <div className='qd-section'>
                <p className='qd-label'>Services</p>
                <div className='qd-services-grid'>
                  {SERVICES_OPTIONS.map(s => (
                    <button
                      key={s}
                      className={`qd-service-btn ${form.services.includes(s)?'qd-service-btn--active':''}`}
                      onClick={()=>toggleService(s)}
                      type='button'
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className='qd-section'>
                <p className='qd-label'>Requirement</p>
                <textarea className='qd-edit-field' rows={3} value={form.requirement} onChange={e=>set('requirement',e.target.value)} placeholder='What do they need?'/>
              </div>

              <div className='qd-section'>
                <div className='qd-edit-row'>
                  <div className='qd-edit-field'>
                    <label>Budget</label>
                    <select value={form.budget} onChange={e=>set('budget',e.target.value)}>
                      {BUDGETS.map(b=><option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className='qd-edit-field'>
                    <label>Timeline</label>
                    <select value={form.timeline} onChange={e=>set('timeline',e.target.value)}>
                      {TIMELINES.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className='qd-section'>
                <p className='qd-label'>Notes</p>
                <textarea className='qd-edit-field' rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder='Internal notes...'/>
              </div>

              {/* Booking reschedule */}
              <div className='qd-section'>
                <p className='qd-label'>
                  Discovery Session
                  {booking && <span className='qd-label-current'> — current: {booking.booking_date} at {booking.booking_time}</span>}
                </p>
                <p className='qd-bp-hint'>
                  {booking ? 'Pick a new slot to reschedule, or leave unchanged.' : 'Assign a session slot.'}
                </p>
                {newDate && newTime && (
                  <div className='qd-new-slot'>
                    ✓ New slot: <strong>{newDate}</strong> at <strong>{fmtTime(newTime)}</strong>
                    <button onClick={()=>{setNewDate(null);setNewTime(null)}}>✕</button>
                  </div>
                )}
                <BookingPicker
                  currentDate={booking?.booking_date}
                  currentTime={booking?.booking_time}
                  excludeBookingId={booking?.id}
                  onPick={(d,t)=>{setNewDate(d);setNewTime(t)}}
                />
              </div>

              {saveErr && <p className='qd-save-err'>⚠ {saveErr}</p>}

              <div className='qd-edit-actions'>
                <button className='qd-btn-cancel' onClick={cancelEdit}>Cancel</button>
                <button className='qd-btn-save' onClick={saveEdits} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            /* ══ VIEW MODE ══════════════════════════════ */
            <>
              {/* Contact */}
              <div className='qd-section'>
                <p className='qd-label'>Contact</p>
                <div className='qd-contact-row'>
                  <a href={`mailto:${quote.email}`} className='qd-contact-chip'>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,4 12,13 2,4"/></svg>
                    {quote.email}
                  </a>
                  <a href={`tel:${quote.phone}`} className='qd-contact-chip'>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.59a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    {quote.phone}
                  </a>
                </div>
              </div>

              {/* Status */}
              <div className='qd-section'>
                <p className='qd-label'>Status</p>
                <div className='qd-status-row'>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      className='qd-status-btn'
                      style={status === key ? {
                        background: STATUS_COLORS[key].bg,
                        borderColor: STATUS_COLORS[key].border,
                        color: STATUS_COLORS[key].text,
                      } : {}}
                      onClick={() => changeStatus(key)}
                      disabled={saving}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div className='qd-section'>
                <p className='qd-label'>Services Requested</p>
                <div className='qd-chips'>
                  {normalizeServices(quote.services).map(s => (
                    <span key={s} className='qd-chip'>{s}</span>
                  ))}
                </div>
              </div>

              {/* Requirement */}
              <div className='qd-section'>
                <p className='qd-label'>Problem to Solve</p>
                <p className='qd-text'>{quote.requirement}</p>
              </div>

              {/* Company desc */}
              {quote.company_desc && (
                <div className='qd-section'>
                  <p className='qd-label'>About Their Company</p>
                  <p className='qd-text'>{quote.company_desc}</p>
                </div>
              )}

              {/* Budget + Timeline */}
              <div className='qd-section qd-two-col'>
                <div>
                  <p className='qd-label'>Budget</p>
                  <p className='qd-mono'>{quote.budget}</p>
                </div>
                <div>
                  <p className='qd-label'>Timeline</p>
                  <p className={`qd-mono ${quote.timeline?.includes('Rush') ? 'qd-mono--warning' : ''}`}>
                    {quote.timeline}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {quote.notes && (
                <div className='qd-section'>
                  <p className='qd-label'>Additional Notes</p>
                  <p className='qd-text'>{quote.notes}</p>
                </div>
              )}

              {/* Booking */}
              <div className='qd-section'>
                <p className='qd-label'>Discovery Session</p>
                {booking ? (
                  <div className='qd-booking'>
                    <span className='qd-booking-icon'>📅</span>
                    <div>
                      <p className='qd-booking-date'>{booking.booking_date} at {fmtTime(booking.booking_time)}</p>
                      {booking.notes && <p className='qd-booking-notes'>{booking.notes}</p>}
                    </div>
                  </div>
                ) : (
                  <p className='qd-text' style={{fontStyle:'italic'}}>No session booked yet</p>
                )}
              </div>

              {/* Meta */}
              <div className='qd-section qd-meta'>
                <span>Submitted: {formatDate(quote.submitted_at)}</span>
                <span>ID: #{quote.id}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}